<script setup lang="ts">
import { UiButton } from '@ui'
import type { EventItem } from '~~/types/types'

const { locale } = useI18n()
const localeRoute = useLocaleRoute()

definePageMeta({
  layout: 'base',
  key: (route) => route.fullPath,
})

const { t } = useI18n()

const {
  public: { siteUrl },
} = useRuntimeConfig()

useSeoMeta({
  title: t('belong-token-gating'),
  description: t('token-gate-your-private'),
  ogImage: `${siteUrl}/images/head/snippet_screen.png`,
  twitterTitle: t('belong-token-gating'),
  twitterDescription: t('token-gate-your-private'),
  twitterImage: `${siteUrl}/images/head/snippet_screen.png`,
  twitterCard: 'summary_large_image',
})

const take = 24

const queryData = computed(() => ({
  take,
  locale: locale.value,
}))

// first load
const pages = useState<{ page: number; data: EventItem[] }[]>(
  'events',
  () => []
)
const { data: firstPage, pending } =
  await useNuxtApp().$client.events.find.useQuery(queryData)

const canLoadMore = ref(false)
const isNextPageLoading = ref(false)

watch(
  firstPage,
  (value) => {
    pages.value = []

    if (value?.data.length) {
      if (!pages.value.length) {
        pages.value.push({
          page: 1,
          data: value?.data,
        })
      }

      canLoadMore.value = value.meta.take === value.data.length
    }
  },
  {
    immediate: true,
  }
)

const onClickHandler = async () => {
  isNextPageLoading.value = true

  const page = pages.value.at(-1)?.page

  try {
    if (!page) return

    const { data, meta } = await useNuxtApp().$client.events.find.query({
      page: page + 1,
      ...queryData.value,
    })

    if (!data.length || meta.take !== data.length) {
      throw new Error('No more data')
    }

    pages.value.push({ page: page + 1, data })
  } catch (e) {
    canLoadMore.value = false
  }

  isNextPageLoading.value = false
}
</script>

<template>
  <div class="container mx-auto">
    <section class="flex">
      <div>
        <!-- <Breadcrumbs class="breadcrumbs">
          <template #breadcrumb="{ to, title, first }">
            <span v-if="!first" class="px-2">›</span>
            <NuxtLink :to="localeRoute(to)">
              {{ title }}
            </NuxtLink>
          </template>
        </Breadcrumbs> -->
      </div>
      <div></div>
    </section>

    <div class="py-4">
      <div class="w-full h-0.25 bg-[#303030]" />
    </div>

    <section>
      <div
        v-if="pages.length"
        class="grid grid-flow-row gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      >
        <template v-for="{ data } in pages">
          <div
            v-for="event in data"
            class="my-4 p-2 rounded-xl shadow-lg bg-[#111] duration-300 hover:-translate-y-1"
          >
            <NuxtLink
              :to="localeRoute(`/events/${event.id}`)"
              class="cursor-pointer"
            >
              <NuxtImg
                v-if="event?.coverImage?.startsWith('http')"
                :src="event.coverImage"
                alt=""
                class="rounded-t h-32 w-full object-cover"
              />
              <NuxtImg
                v-else-if="event.coverImage"
                provider="belong"
                :src="`/up/activities/${event.id}_${event.coverImage}.jpg`"
                alt=""
                class="rounded-t h-32 w-full object-cover"
              />
              <h3 class="pt-4 line-clamp-2">{{ event.name }}</h3>

              <small class="leading-5 line-clamp-3 text-[#96A2A6]">
                <div class="py-1">
                  {{ $dayjs(event.start).format('llll') }}
                  &ndash;
                  {{ $dayjs(event.end).format('llll') }}
                </div>

                <div>{{ event.description }}</div>
              </small>
            </NuxtLink>
          </div>
        </template>
      </div>
      <div v-else>No events</div>

      <div v-if="isNextPageLoading || pending" class="w-20 mx-auto my-8">
        <Lottie src="/lottie/loading.json" autoplay loop />
      </div>
    </section>

    <div class="py-4"><div class="w-full h-0.25 bg-[#303030]" /></div>

    <section v-if="canLoadMore">
      <div class="flex justify-center">
        <UiButton
          @click="onClickHandler()"
          variant="gray-outline-website"
          class="max-w-100 w-full"
        >
          <p class="text-white text-center">
            {{ t('load-more') }}
          </p>
        </UiButton>
      </div>
    </section>
  </div>
</template>

<style scoped>
@reference "tailwindcss";
.breadcrumbs {
  &:deep(ul) {
    @apply flex flex-row;

    li {
      @apply text-sm;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
